import { useQuery } from "@tanstack/react-query";
import { salesDb } from "@/integrations/supabase/sales-client";
import { supabase } from "@/integrations/supabase/client";

export type AssignmentRow = {
  id: string;
  event_id: string;
  fishsix_student_code: string | null;
  esc_student_code: string | null;
  cashback_id: string | null;
  refund_cashback_id: string | null;
  assigned_by: string | null;
  assigned_at: string;
  unassigned_at: string | null;
  unassigned_by: string | null;
  status: string;
  student_first_name?: string | null;
  student_last_name?: string | null;
  student_nickname?: string | null;
  assigned_by_name?: string | null;
};

export const useEventAssignments = (eventId: string | null) => {
  return useQuery({
    enabled: !!eventId,
    queryKey: ["event-assignments", eventId],
    queryFn: async () => {
      const { data, error } = await salesDb()
        .from("event_assignments")
        .select("*")
        .eq("event_id", eventId)
        .eq("status", "active")
        .order("assigned_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as AssignmentRow[];

      const codes = Array.from(
        new Set(rows.map((r) => r.fishsix_student_code).filter(Boolean) as string[])
      );
      const userIds = Array.from(
        new Set(rows.map((r) => r.assigned_by).filter(Boolean) as string[])
      );

      const [studentsRes, profilesRes] = await Promise.all([
        codes.length
          ? supabase.from("fishsix_students").select("code,first_name,last_name,nickname").in("code", codes)
          : Promise.resolve({ data: [], error: null } as any),
        userIds.length
          ? supabase.from("profiles").select("id,full_name,email").in("id", userIds)
          : Promise.resolve({ data: [], error: null } as any),
      ]);

      const sMap = new Map<string, any>();
      (studentsRes.data ?? []).forEach((s: any) => sMap.set(s.code, s));
      const pMap = new Map<string, any>();
      (profilesRes.data ?? []).forEach((p: any) => pMap.set(p.id, p));

      return rows.map((r) => {
        const s = r.fishsix_student_code ? sMap.get(r.fishsix_student_code) : null;
        const p = r.assigned_by ? pMap.get(r.assigned_by) : null;
        return {
          ...r,
          student_first_name: s?.first_name ?? null,
          student_last_name: s?.last_name ?? null,
          student_nickname: s?.nickname ?? null,
          assigned_by_name: p?.full_name ?? p?.email ?? null,
        } as AssignmentRow;
      });
    },
  });
};
